// ─────────────────────────────────────────────────────────────────────────────
// THE SHELF — rungs 0–3 in one state machine.
//
//   rung 0/1  ANONYMOUS — the published set, reverse-chronological. No bearer,
//             no runtime call, nothing personal. What every visitor sees.
//   rung 2/3  PROFILED — the same set, reordered by the fan's OWN agent, each
//             card carrying the runtime's why-string.
//
// The signature tap-toggle flips between these two orderings of the SAME cards.
// Both are held in state simultaneously so the toggle is instant and, more
// importantly, so the difference is legible: same shelf, two orders, and the
// fan can see exactly what their agent changed.
//
// Ordering authority lives in the runtime. After a chip press we re-call
// `/app/rank-candidates` rather than re-sorting locally, so what the fan sees
// is always the profile's real verdict.
// ─────────────────────────────────────────────────────────────────────────────

import {useCallback, useEffect, useState} from 'react'

import {
  loadCandidateSet,
  reverseChronological,
  cardRankingId,
  type Card,
  type CandidateSet,
} from './candidateSet'
import {rankCandidates, applyOrder, RankError} from './rankCandidates'
import {chipPatch, patchInterests, type ChipDirection} from './interests'
import {emit as emitBeacon, topTopics} from './telemetry'

export interface ShelfCard {
  card: Card
  /** The runtime why-string — present only on the profiled shelf. */
  why: string | null
}

export type ShelfMode = 'anonymous' | 'profiled'

export interface ShelfState {
  set: CandidateSet | null
  anonymous: ShelfCard[]
  profiled: ShelfCard[] | null
  loading: boolean
  ranking: boolean
  /** Cards hidden by a mute (hide-not-rank), for an honest count in the UI. */
  filtered: number
  /** The ranking degraded — we fall back to the anonymous order, and say so. */
  degraded: boolean
  error: string | null
}

const EMPTY: ShelfState = {
  set: null,
  anonymous: [],
  profiled: null,
  loading: true,
  ranking: false,
  filtered: 0,
  degraded: false,
  error: null,
}

/**
 * @param getToken resolves the fan's bearer, or null when signed out.
 */
export function useShelf(getToken: () => Promise<string | null>) {
  const [state, setState] = useState<ShelfState>(EMPTY)

  // 1. Load the published set (bare, same-origin, anonymous).
  useEffect(() => {
    let live = true
    loadCandidateSet()
      .then(set => {
        if (!live) return
        setState(s => ({
          ...s,
          set,
          anonymous: reverseChronological(set.cards).map(card => ({card, why: null})),
          loading: false,
        }))
        // counts-only telemetry (flag-dark): topic-level 'viewed' for the
        // shelf's leading topics — no identifier travels with these.
        for (const topic of topTopics(reverseChronological(set.cards), 6)) {
          emitBeacon('viewed', {topic, candidateSetVersion: set.version})
        }
      })
      .catch(err => {
        if (!live) return
        setState(s => ({...s, loading: false, error: String(err?.message ?? err)}))
      })
    return () => {
      live = false
    }
  }, [])

  // 2. Rank inside the fan's own agent. Called on sign-in and after a chip.
  const rank = useCallback(
    async (set: CandidateSet | null) => {
      if (!set) return
      const token = await getToken()
      if (!token) {
        setState(s => ({...s, profiled: null, ranking: false}))
        return
      }

      setState(s => ({...s, ranking: true, error: null}))
      try {
        const result = await rankCandidates(
          {cards: set.cards, publisher: set.publisher, version: set.version},
          token,
        )
        const joined = applyOrder(set.cards, result.order, cardRankingId)
        setState(s => ({
          ...s,
          profiled: joined.map(({card, why}) => ({card, why})),
          filtered: result.filtered,
          degraded: result.degraded,
          ranking: false,
        }))
        // counts-only telemetry (flag-dark): the topics of the fan's OWN top
        // three, as an anonymous count. The ordering itself never leaves the
        // page; the random session id joins to nothing.
        for (const topic of topTopics(joined.map(j => j.card), 3)) {
          emitBeacon('ranked-top3', {topic, candidateSetVersion: set.version})
        }
      } catch (err) {
        // Fail soft: the shelf stays readable in the anonymous order. We never
        // fabricate an ordering to cover a failed ranking.
        const message =
          err instanceof RankError && err.status === 401
            ? 'Your session expired — sign in again to see your shelf.'
            : String((err as Error)?.message ?? err)
        setState(s => ({...s, ranking: false, degraded: true, error: message}))
      }
    },
    [getToken],
  )

  // 3. A chip press: patch the profile, then re-rank so the reorder is the
  //    runtime's verdict rather than a local guess.
  const sendChip = useCallback(
    async (card: Card, direction: ChipDirection) => {
      const token = await getToken()
      if (!token) return
      const topics = Array.isArray(card.topics) ? card.topics : []
      if (!topics.length) return

      try {
        await patchInterests(chipPatch(topics, direction), token, {
          note: `show-space chip: ${direction} like "${card.title}"`,
        })
        await rank(state.set)
      } catch (err) {
        setState(s => ({...s, error: String((err as Error)?.message ?? err)}))
      }
    },
    [getToken, rank, state.set],
  )

  return {state, rank, sendChip}
}
